import { useEffect } from 'react';
import { API_ENDPOINTS } from 'config/ApiConfig';
import useAuthStore from 'Store/useAuthStore';
import useNotificationStoreV2 from 'Store/useNotificationStoreV2';
import { fetchPersistentNotifications } from 'Api/apiService';
import { isTokenValid } from 'Utils/tokenValidation';

// Any Unix-seconds value below this (year 2000) is considered bogus
const MIN_VALID_TS = 946684800;

/**
 * Resolve a Unix timestamp in SECONDS from the various shapes the backend sends:
 *
 *  Source A — send_notification():
 *    record_insert_time_stamp: "2026-04-09T04:14:54.406386+00:00"  ← ISO string, 6-decimal microseconds
 *
 *  Source B — send_user_notification():
 *    created_at: 1744172094   ← plain Unix integer (seconds)
 *
 * Returns null when no valid timestamp is found — callers must handle null gracefully.
 */
const resolveTimestamp = (data) => {
  const candidates = [
    data.record_insert_time_stamp,
    data.created_at,
    data.timestamp,
    data.ts,
  ];

  for (const raw of candidates) {
    if (raw == null || raw === '') continue;

    let seconds;
    const asNum = Number(raw);

    if (!isNaN(asNum)) {
      // Numeric string or number — could be seconds or milliseconds
      seconds = asNum > 1e12 ? Math.floor(asNum / 1000) : Math.floor(asNum);
    } else if (typeof raw === 'string') {
      // ISO date string from Python's datetime.isoformat()
      // Python produces 6 decimal places (microseconds); JS Date only handles 3 (ms)
      // Truncate extra decimals before parsing so Date.parse works in all browsers
      const safe = raw.replace(/(\.\d{3})\d+/, '$1');
      const ms = Date.parse(safe);
      if (isNaN(ms)) continue;
      seconds = Math.floor(ms / 1000);
    } else {
      continue;
    }

    if (seconds > MIN_VALID_TS) return seconds;
  }

  return null;
};

const normaliseSSEPayload = (data) => ({
  id: data.sb_notification_unique_id || data.id || String(Date.now()),
  message: data.message || '',
  message_type: data.message_type || 'info',
  entity_id: data.entity_id || null,
  context_url: data.context_url || null,
  created_at: resolveTimestamp(data),
});

const BASE_RECONNECT_DELAY = 2000;
const MAX_RECONNECT_DELAY = 30000;
// Random spread added to each reconnect so tabs cut at the same moment (deploy,
// lifetime rotation) don't all hit the server in the same instant.
const RECONNECT_JITTER = 1000;
// Retrying with the same token can never succeed (flask-jwt-extended uses 422 for bad tokens).
// Stop and wait for the token to change — the effect re-runs with the new one.
const AUTH_FAILURE_STATUSES = new Set([401, 403, 422]);
// The backend sends ": keepalive" every ~10s while idle. If nothing at all arrives
// within this window the connection is half-open (laptop sleep, network drop).
const STALE_STREAM_TIMEOUT = 45000;
// Background tabs release their stream after this grace period and resume when visible.
const HIDDEN_DISCONNECT_DELAY = 5 * 60 * 1000;

/**
 * Pull notifications from the DB and merge any we missed while the stream was down
 * (server-side rotation, stale reconnect, tab resumed from background).
 */
const resyncNotifications = () => {
  fetchPersistentNotifications()
    .then((messages) => {
      if (!messages?.length) return;
      useNotificationStoreV2.getState().mergeNotifications(messages.map(normaliseSSEPayload));
    })
    .catch((err) => console.error('[GlobalSSE] resync error:', err));
};

/**
 * useGlobalSSE
 *
 * Manages the persistent notification stream for the bell icon.
 * Lifecycle: session (login → logout). The backend caps each stream's lifetime and
 * ends it with `event: end`; this hook reconnects transparently and resyncs from the DB.
 *
 * Connection hygiene (so the server never holds abandoned streams):
 *  - Reconnects when no data/keepalive arrives within STALE_STREAM_TIMEOUT.
 *  - Releases the stream when the tab stays hidden for HIDDEN_DISCONNECT_DELAY,
 *    and immediately on pagehide; resumes when the page is visible again.
 *
 * Uses a fetch-based SSE reader instead of EventSourcePolyfill to ensure all
 * network errors are caught and never surface as unhandled promise rejections.
 *
 * Task progress streams (job creation, workflow) are NOT handled here.
 * They are short-lived and owned by the component that starts the job (JobsLibraryV2).
 */
const useGlobalSSE = () => {
  const accessToken = useAuthStore((state) => state.accessToken);

  // On mount: load persisted notifications from DB (source of truth)
  useEffect(() => {
    if (!accessToken || !isTokenValid(accessToken, 'useGlobalSSE')) return;
    fetchPersistentNotifications()
      .then((messages) => {
        const normalised = (messages || []).map(normaliseSSEPayload);
        useNotificationStoreV2.getState().setNotifications(normalised);
      })
      .catch((err) => console.error('[GlobalSSE] fetch persistent error:', err));
  }, [accessToken]);

  // Notification stream — session lifetime, reconnects with exponential backoff
  useEffect(() => {
    if (!accessToken || !isTokenValid(accessToken, 'useGlobalSSE')) return;

    let cancelled = false; // effect torn down (logout / token change / unmount)
    let paused = false; // stream released while the page is hidden
    let abortController = null;
    let reconnectTimer = null;
    let staleTimer = null;
    let hiddenTimer = null;
    let reconnectDelay = BASE_RECONNECT_DELAY;
    let hasConnected = false;

    const isActive = () => !cancelled && !paused;

    const armStaleTimer = (controller) => {
      clearTimeout(staleTimer);
      staleTimer = setTimeout(() => {
        console.warn('[GlobalSSE] no data or keepalive received — reconnecting');
        controller.abort();
      }, STALE_STREAM_TIMEOUT);
    };

    const scheduleReconnect = () => {
      if (!isActive()) return;
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => {
        reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
        connect();
      }, reconnectDelay + Math.random() * RECONNECT_JITTER);
    };

    const connect = async () => {
      if (!isActive()) return;
      // Token expired since the effect started — don't spin on 401s; a new token re-runs the effect
      if (!isTokenValid(accessToken, 'useGlobalSSE')) return;

      const controller = new AbortController();
      abortController = controller;
      // Ignore late callbacks from a connection that has since been replaced or released
      const isCurrent = () => isActive() && abortController === controller;

      try {
        const response = await fetch(API_ENDPOINTS.NOTIFICATION_STREAM, {
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
        });

        if (AUTH_FAILURE_STATUSES.has(response.status)) {
          console.warn(`[GlobalSSE] stream rejected credentials (${response.status}) — not retrying`);
          return;
        }

        if (!response.ok) {
          console.error(`[GlobalSSE] stream responded ${response.status} — will retry`);
          if (isCurrent()) scheduleReconnect();
          return;
        }

        // Catch up on anything sent while disconnected
        if (hasConnected) resyncNotifications();
        hasConnected = true;
        armStaleTimer(controller);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let serverEnded = false;
        let receivedBytes = false;

        while (!serverEnded) {
          const { done, value } = await reader.read();
          if (done || !isCurrent()) break;

          // Reset backoff only once the stream proves healthy (first bytes), so a server
          // that answers 200 and closes immediately still backs off instead of looping at 2s.
          if (!receivedBytes) {
            receivedBytes = true;
            reconnectDelay = BASE_RECONNECT_DELAY;
          }
          // Any bytes (data or keepalive comment) prove the connection is alive
          armStaleTimer(controller);

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete trailing line

          for (const rawLine of lines) {
            const line = rawLine.replace(/\r$/, '');

            // Server-initiated rotation (max stream lifetime reached)
            if (/^event:\s?end$/.test(line)) {
              serverEnded = true;
              break;
            }

            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (!data.message) continue;
              useNotificationStoreV2.getState().addNotification(normaliseSSEPayload(data));
            } catch (_) {
              // ignore unparseable SSE lines
            }
          }
        }

        clearTimeout(staleTimer);
        if (serverEnded) controller.abort(); // release the socket now rather than waiting for close
        if (isCurrent()) scheduleReconnect();
      } catch (err) {
        clearTimeout(staleTimer);
        // Intentional aborts (teardown / pause) are filtered by isCurrent();
        // an AbortError while still current means the stale watchdog fired.
        if (!isCurrent()) return;
        if (err.name !== 'AbortError') {
          console.error('[GlobalSSE] connection error:', err.message, '— will retry');
        }
        scheduleReconnect();
      }
    };

    const pause = () => {
      if (paused || cancelled) return;
      paused = true;
      clearTimeout(reconnectTimer);
      clearTimeout(staleTimer);
      abortController?.abort();
    };

    const resume = () => {
      if (!paused || cancelled) return;
      paused = false;
      reconnectDelay = BASE_RECONNECT_DELAY;
      connect();
    };

    const handleVisibilityChange = () => {
      clearTimeout(hiddenTimer);
      if (document.visibilityState === 'hidden') {
        hiddenTimer = setTimeout(pause, HIDDEN_DISCONNECT_DELAY);
      } else {
        resume();
      }
    };

    // Restored from the back/forward cache — pagehide released the stream
    const handlePageShow = (event) => {
      if (event.persisted) resume();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', pause);
    window.addEventListener('pageshow', handlePageShow);

    connect();
    if (document.visibilityState === 'hidden') handleVisibilityChange();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      clearTimeout(staleTimer);
      clearTimeout(hiddenTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', pause);
      window.removeEventListener('pageshow', handlePageShow);
      abortController?.abort();
    };
  }, [accessToken]);
};

export default useGlobalSSE;
